'use client'

import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CHECKOUT_MSG, postToParent, resolveHostOrigin } from '../../../checkout/_lib/checkout-embed-messages'
import { attributionSource, type CheckoutSurface } from '../../../checkout/_lib/checkout-surface'
import { buildConfirmationPath } from './_lib/confirmation-link'
import { slotTimeLine } from './_lib/slot-time'
import { InlineWaiverSigner, type InlineWaiverResult } from './inline-waiver-signer'
import { CheckoutPaymentStep, type CheckoutSucceededInfo } from './checkout-payment-step'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type {
  TicketedEventPublic,
  TicketTypePublic,
  TicketedEventCustomQuestionPublic,
  TicketedEventFaqPublic
} from '@school-journey/shared-types'

export interface PublicWaiverRequirement {
  id: string
  label: string
  formPublicId: string
  // Slug of the linked form's public page when guest-signable. Null when
  // the form is not currently distributed via public link or doesn't allow
  // anonymous submissions — in that case the waiver button is disabled.
  formPublicSlug: string | null
  required: boolean
  perAttendee: boolean
  // `at_registration` must be signed before the order submits; the backend
  // surfaces `before_attendance` here too but those are collected later (from
  // the confirmation page), so RSVP must NOT block on them.
  completionTiming: 'at_registration' | 'before_attendance'
  sortOrder: number
}

// A waiver gates ORDER submission only when it is required AND due
// at_registration. before_attendance forms are completed later from the
// confirmation page (U5/U9), and optional waivers never block — so neither
// must stop the RSVP (reviewer P1 #2). Exported for direct unit testing.
export function waiverGatesRegistration(w: Pick<PublicWaiverRequirement, 'required' | 'completionTiming'>): boolean {
  return w.required && w.completionTiming === 'at_registration'
}

// Required `before_attendance` waivers are collected LATER (confirmation page /
// email), not at registration — the completion surface flags them so the buyer
// knows more is coming. Mirror of `waiverGatesRegistration` for the later beat.
function hasBeforeAttendanceForms(waiverRequirements: PublicWaiverRequirement[]): boolean {
  return waiverRequirements.some(w => w.completionTiming === 'before_attendance' && w.required)
}

interface WaiverSignatureState {
  formResponsePublicId: string
  signerName: string
  signerEmail: string
  signedAt: string
  signatureMethod: string
}

interface WaiverSignedMessage {
  type: 'waiver-signed'
  waiverRequirementPublicId: string
  formResponsePublicId: string
  signerName: string
  signerEmail: string
  signedAt: string
  signatureMethod?: string
}

function isWaiverSignedMessage(value: unknown): value is WaiverSignedMessage {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    v.type === 'waiver-signed' &&
    typeof v.waiverRequirementPublicId === 'string' &&
    typeof v.formResponsePublicId === 'string' &&
    typeof v.signerName === 'string' &&
    typeof v.signerEmail === 'string' &&
    typeof v.signedAt === 'string'
  )
}

export interface PublicEventExtras {
  ticketTypes?: TicketTypePublic[]
  customQuestions?: TicketedEventCustomQuestionPublic[]
  faqs?: TicketedEventFaqPublic[]
  waiverRequirements?: PublicWaiverRequirement[]
  requirePerAttendeeData?: boolean
}

interface RsvpFormClientProps {
  schoolSlug: string
  eventPublicId: string
  event: TicketedEventPublic & PublicEventExtras
  // Which surface is rendering this flow (see `CheckoutSurface`). Defaults to
  // the plain public event page: popup waiver signing, navigation to
  // `/confirmation`, no attribution tag. `embed` and `mobile` both sign
  // at-registration waivers INLINE; only `embed` is framed by a host page.
  surface?: CheckoutSurface
  // The host page URL the loader threaded into the iframe src — used to resolve
  // the postMessage target origin and as the attribution `sourceUrl` (U4).
  // Embed-only: the mobile in-app browser has no host page.
  parentUrl?: string | null
  // A waitlist offer being claimed (U13): the form is prefilled from the
  // entry, the email is the entry's and cannot change, and the order posts to
  // the claim endpoint, which consumes the offer's hold.
  claim?: WaitlistClaimPrefill
}

interface WaitlistClaimPrefill {
  entryId: string
  firstName: string
  lastName: string
  email: string
  ticketTypeId: string
  quantity: number
}

// Stepped view machine for the inline-signing surfaces (embed + mobile). The
// plain page always renders the single-page form.
type EmbedView =
  | { kind: 'form' }
  | { kind: 'signing'; requirement: PublicWaiverRequirement }
  // U8: a paid order was created (held awaiting_payment) — render the
  // Payment Element inline before the embed can report orderComplete.
  | { kind: 'payment' }
  | { kind: 'confirmation'; orderId: string; outstandingForms: boolean }

// U8: state carried from a successful paid-order creation into the payment
// step. `createdAtMs` anchors the client-side seat-hold countdown.
interface PaymentContext {
  referenceCode: string
  clientSecret: string
  stripeAccountId: string
  createdAtMs: number
}

interface ProfilePrefill {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
}

interface AttendeeRow {
  firstName: string
  lastName: string
  email: string
  grade: string
}

async function fetchProfilePrefill(): Promise<ProfilePrefill | null> {
  // Opportunistic pre-fill for Parent-in-App: 401s silently for guests.
  try {
    const base = process.env.NEXT_PUBLIC_BACKEND_URL
    if (!base) return null
    const res = await fetch(`${base}/v1/users/me`, { credentials: 'include' })
    if (!res.ok) return null
    const json = (await res.json()) as { data?: ProfilePrefill }
    return json.data ?? null
  } catch {
    return null
  }
}

function emptyAttendee(): AttendeeRow {
  return { firstName: '', lastName: '', email: '', grade: '' }
}

/**
 * How much of a ticket row is left, worded once for both renderers (R1, R2).
 *
 * The quantity rows and the slot rows below used to disagree: slot rows printed
 * `capacityRemaining` on both sides of the fraction — "28 of 28 open" for a
 * 30-seat tier with two taken — while quantity rows showed a bare count, and
 * only once fewer than ten remained. One helper is what keeps them from
 * drifting again.
 *
 * `capacity` is optional on the public contract so an older backend cannot fail
 * the mobile client's runtime parse; when it is missing we show the remainder
 * alone rather than a fraction of one number by itself.
 *
 * Callers own the sold-out case — it reads "Sold out" on a ticket and "Filled"
 * on a volunteer slot, and never as a zero count.
 */
function availabilityLabel(tier: { capacity?: number; capacityRemaining: number }): string {
  return tier.capacity != null ? `${tier.capacityRemaining} of ${tier.capacity} left` : `${tier.capacityRemaining} left`
}

export function RsvpFormClient({
  schoolSlug,
  eventPublicId,
  event,
  surface = 'page',
  parentUrl,
  claim
}: RsvpFormClientProps) {
  const router = useRouter()
  const isSignup = event.kind === 'signup'
  const isApplication = event.kind === 'application'
  const apiBase = process.env.NEXT_PUBLIC_BACKEND_URL

  // The two axes the surfaces vary along, named so each branch below says which
  // one it depends on. `framed` is the narrow one: only the embed has a host
  // page listening, so only the embed may replace navigation with postMessage.
  const framed = surface === 'embed'
  const inlineWaiver = surface !== 'page'

  // Embed-mode plumbing. hostOrigin is resolved on the client (referrer/param)
  // and used as the explicit postMessage targetOrigin (KTD3).
  const [embedView, setEmbedView] = useState<EmbedView>({ kind: 'form' })
  const hostOriginRef = useRef<string>('*')
  useEffect(() => {
    if (framed) hostOriginRef.current = resolveHostOrigin(parentUrl ?? null)
  }, [framed, parentUrl])

  const ticketTypes = useMemo(
    () => (event.ticketTypes ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [event.ticketTypes]
  )
  const customQuestions = useMemo(
    () => (event.customQuestions ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [event.customQuestions]
  )
  const waiverRequirements = useMemo(
    () => (event.waiverRequirements ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [event.waiverRequirements]
  )

  const [firstName, setFirstName] = useState(claim?.firstName ?? '')
  const [lastName, setLastName] = useState(claim?.lastName ?? '')
  const [email, setEmail] = useState(claim?.email ?? '')
  // Map of ticketTypeId -> quantity selected.
  const [quantities, setQuantities] = useState<Record<string, number>>(
    claim ? { [claim.ticketTypeId]: claim.quantity } : {}
  )
  const [answers, setAnswers] = useState<Record<string, string | string[] | boolean>>({})
  const [attendees, setAttendees] = useState<AttendeeRow[]>([])
  // Map of waiverRequirementPublicId -> signature state once signed.
  const [signatures, setSignatures] = useState<Record<string, WaiverSignatureState>>({})
  // Map of waiverRequirementPublicId -> 'opening' | 'pending'. 'opening' is
  // set synchronously in the click handler (popup is open, fetch in flight);
  // 'pending' once the popup has navigated and we're waiting on postMessage.
  const [waiverPending, setWaiverPending] = useState<Record<string, 'opening' | 'pending'>>({})
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // U8: set when order creation returns a paid order's clientSecret +
  // stripeAccountId. Non-embed switches to <CheckoutPaymentStep> in place of
  // the form; embed switches embedView to 'payment'.
  const [paymentContext, setPaymentContext] = useState<PaymentContext | null>(null)

  // Buyer email + name lock once any waiver signature has been captured.
  // Without this, changing email after signing would cause a backend
  // WAIVER_SIGNATURE_INVALID rejection at order-submit (the recorded
  // signerEmail wouldn't match the new buyerEmail).
  const buyerLocked = Object.keys(signatures).length > 0
  // A claim's email is the waitlist entry's: the claim endpoint refuses any other.
  const emailLocked = buyerLocked || !!claim

  function clearAllSignatures() {
    setSignatures({})
  }

  // Embed inline-signing completed: record the signature (same shape the popup
  // handshake produces) and return to the form step with all data intact.
  function onInlineSigned(requirementId: string, result: InlineWaiverResult) {
    setSignatures(prev => ({ ...prev, [requirementId]: result }))
    setEmbedView({ kind: 'form' })
  }

  function openWaiverPopup(requirement: PublicWaiverRequirement) {
    setError(null)

    if (!requirement.formPublicSlug) {
      setError('This waiver isn’t available to sign right now. Please contact the school.')
      return
    }
    if (requirement.perAttendee) {
      setError('Per-attendee waivers are not yet supported.')
      return
    }
    if (!email.trim() || !firstName.trim() || !lastName.trim()) {
      setError('Please enter your name and email before signing the waiver.')
      return
    }

    // Embed and mobile sign INLINE — no popup, which neither a framed page nor
    // an in-app browser can reliably open. Switch to the in-place signing step;
    // back-nav returns here with all ticket/buyer state intact (it lives in
    // this component).
    if (inlineWaiver) {
      setEmbedView({ kind: 'signing', requirement })
      return
    }

    // Open the popup synchronously inside the click handler — BEFORE the
    // async navigation — to preserve the user gesture. Browsers block
    // window.open() called after `await`. The popup loads about:blank
    // first; we then swap its location to the signing URL with context
    // params encoded so the signing page can post the result back.
    const popup = window.open('about:blank', `waiver-${requirement.id}`, 'width=720,height=900')
    if (!popup) {
      setError('Please allow popups for this page so the waiver can open.')
      return
    }

    setWaiverPending(prev => ({ ...prev, [requirement.id]: 'opening' }))

    const params = new URLSearchParams({
      waiverContext: '1',
      ticketedEventPublicId: eventPublicId,
      waiverRequirementPublicId: requirement.id,
      signerEmail: email.trim(),
      signerName: `${firstName.trim()} ${lastName.trim()}`,
      eventLabel: requirement.label
    })
    const signingUrl = `/forms/p/${encodeURIComponent(requirement.formPublicSlug)}?${params.toString()}`
    const expectedOrigin = window.location.origin

    function cleanup() {
      window.removeEventListener('message', onMessage)
      try {
        popup?.close()
      } catch {
        /* noop */
      }
      setWaiverPending(prev => {
        const next = { ...prev }
        delete next[requirement.id]
        return next
      })
    }

    function onMessage(event: MessageEvent) {
      if (event.origin !== expectedOrigin) return
      if (!isWaiverSignedMessage(event.data)) return
      if (event.data.waiverRequirementPublicId !== requirement.id) return

      setSignatures(prev => ({
        ...prev,
        [requirement.id]: {
          formResponsePublicId: event.data.formResponsePublicId,
          signerName: event.data.signerName,
          signerEmail: event.data.signerEmail,
          signedAt: event.data.signedAt,
          signatureMethod: event.data.signatureMethod ?? 'typed'
        }
      }))
      cleanup()
    }

    window.addEventListener('message', onMessage)

    try {
      popup.location.replace(signingUrl)
      setWaiverPending(prev => ({ ...prev, [requirement.id]: 'pending' }))
    } catch {
      setError('Could not open the waiver. Please try again.')
      cleanup()
    }
  }

  useEffect(() => {
    fetchProfilePrefill().then(profile => {
      if (!profile) return
      if (profile.firstName) setFirstName(prev => prev || profile.firstName!)
      if (profile.lastName) setLastName(prev => prev || profile.lastName!)
      if (profile.email) setEmail(prev => prev || profile.email!)
    })
  }, [])

  const totalQuantity = Object.values(quantities).reduce((sum, n) => sum + (n || 0), 0)

  // Resize attendee rows when total quantity changes (only when per-attendee data required).
  useEffect(() => {
    if (!event.requirePerAttendeeData) return
    setAttendees(prev => {
      if (prev.length === totalQuantity) return prev
      if (totalQuantity > prev.length) {
        return [...prev, ...Array.from({ length: totalQuantity - prev.length }, emptyAttendee)]
      }
      return prev.slice(0, totalQuantity)
    })
  }, [totalQuantity, event.requirePerAttendeeData])

  const orderPerOrderMax = Math.min(event.perOrderMax, event.capacityRemaining)

  function setQty(typeId: string, value: number, perTypeMax: number) {
    const clamped = Math.max(0, Math.min(value, perTypeMax))
    setQuantities(prev => ({ ...prev, [typeId]: clamped }))
  }

  // Group ticket types by groupLabel for signup-kind events.
  const groupedTypes = useMemo(() => {
    if (!isSignup) return null
    const groups = new Map<string, TicketTypePublic[]>()
    for (const t of ticketTypes) {
      const key = t.groupLabel ?? ''
      const list = groups.get(key) ?? []
      list.push(t)
      groups.set(key, list)
    }
    return Array.from(groups.entries())
  }, [isSignup, ticketTypes])

  function validate(): string | null {
    if (!email || !firstName || !lastName) return 'Please fill in your contact details.'
    if (totalQuantity < 1) return 'Please select at least one spot.'
    if (totalQuantity > orderPerOrderMax) return `Quantity must be at most ${orderPerOrderMax}.`
    for (const q of customQuestions) {
      if (!q.required) continue
      const v = answers[q.id]
      const empty =
        v === undefined ||
        v === null ||
        (typeof v === 'string' && v.trim() === '') ||
        (Array.isArray(v) && v.length === 0) ||
        (q.type === 'checkbox' && v === false)
      if (empty) return `Please answer: ${q.prompt}`
    }
    if (event.requirePerAttendeeData) {
      for (let i = 0; i < attendees.length; i++) {
        const a = attendees[i]
        if (!a.firstName || !a.lastName) return `Please fill attendee ${i + 1} name.`
      }
    }
    for (const w of waiverRequirements) {
      // Only at_registration waivers gate the order (see waiverGatesRegistration).
      // before_attendance forms are completed later from the confirmation page
      // (U5/U9), so requiring a signature here would defeat the timing.
      if (waiverGatesRegistration(w) && !signatures[w.id]) {
        return `Please sign waiver: ${w.label}`
      }
    }
    return null
  }

  // Non-embed confirmation page path (relative — safe for router.push).
  // BE-TICKETS-002: carries buyer email so the confirmation lookup can pass
  // `?email=` through to the backend, which requires a buyer-email match.
  //
  // The construction itself lives in `_lib/confirmation-link.ts` so the
  // my-tickets page links to the same place the same way rather than growing a
  // fourth copy of it (plan 2026-07-31-002, U3).
  function confirmationPath(referenceCode: string): string {
    return buildConfirmationPath({ schoolSlug, eventPublicId, referenceCode, buyerEmail: email, kind: event.kind })
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    const v = validate()
    if (v) {
      setError(v)
      return
    }
    setSubmitting(true)
    try {
      const base = process.env.NEXT_PUBLIC_BACKEND_URL
      const items = Object.entries(quantities)
        .filter(([, n]) => n > 0)
        .map(([ticketTypePublicId, quantity]) => ({ ticketTypePublicId, quantity }))

      const body: Record<string, unknown> = {
        buyerEmail: email,
        buyerFirstName: firstName,
        buyerLastName: lastName,
        items,
        answers: customQuestions
          .filter(q => answers[q.id] !== undefined)
          .map(q => ({ questionPublicId: q.id, value: answers[q.id] }))
      }
      if (event.requirePerAttendeeData) body.attendees = attendees
      if (waiverRequirements.length > 0) {
        body.waiverSignatures = waiverRequirements
          .filter(w => signatures[w.id])
          .map(w => {
            const sig = signatures[w.id]
            return {
              waiverRequirementPublicId: w.id,
              formResponsePublicId: sig.formResponsePublicId,
              signerName: sig.signerName,
              signerEmail: sig.signerEmail,
              signedAt: sig.signedAt,
              signatureMethod: sig.signatureMethod || 'typed'
            }
          })
      }
      // Surface attribution (U4/R11): logged server-side, not persisted. `_hp`
      // is the anti-abuse honeypot (empty for real submits). The plain page
      // sends none of these, so the fields stay absent. `sourceUrl` is embed-only
      // — it is the HOST page, which mobile does not have.
      const source = attributionSource(surface)
      if (source) {
        body.source = source
        if (parentUrl) body.sourceUrl = parentUrl
        body._hp = ''
      }

      const eventPath = `${base}/v1/ticketed-events/by-slug/${encodeURIComponent(schoolSlug)}/${encodeURIComponent(eventPublicId)}`
      const res = await fetch(
        claim ? `${eventPath}/waitlist/${encodeURIComponent(claim.entryId)}/claim` : `${eventPath}/orders`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // Send the Clerk cookie so a signed-in buyer's order is attributed to
          // them (U3/KTD3) — the same `credentials: 'include'` that
          // `fetchProfilePrefill()` above already uses against the same origin.
          // WITHOUT this the backend's session branch is dead code. Guests send
          // no cookie and are unaffected; in the embedded iframe the cookie is
          // third-party and blocked by the browser regardless (KTD4), so those
          // orders attribute by buyer email like any guest.
          credentials: 'include',
          body: JSON.stringify(body)
        }
      )
      if (!res.ok) {
        // Embed submit-failure (R6): keep all entered data (state is retained),
        // signal the loader, and offer Retry — but do NOT post orderComplete.
        if (framed) postToParent(CHECKOUT_MSG.SUBMIT_FAILED, eventPublicId, hostOriginRef.current)
        const json = (await res.json().catch(() => ({}))) as {
          error?: string | { message?: string; type?: string }
        }
        if (res.status === 409 && typeof json.error === 'object' && json.error?.type === 'WAITLIST_OFFER_UNAVAILABLE') {
          setError('This offer has expired or has already been claimed.')
          return
        }
        if (res.status === 409) {
          setError('Sorry — capacity changed before your order was confirmed. Please refresh.')
          return
        }
        const message =
          typeof json.error === 'string'
            ? json.error
            : (json.error?.message ?? 'Something went wrong. Please try again.')
        setError(message)
        return
      }
      const json = (await res.json()) as {
        referenceCode: string
        // U8: present only for a paid order (status='awaiting_payment') —
        // absent for free/application orders, which carry no charge.
        clientSecret?: string
        stripeAccountId?: string
      }

      // U8 (R12/R14): a paid order was created and its seat held — route into
      // the Payment Element step instead of navigating/completing yet. The
      // order is NOT confirmed until the Stripe webhook fires (KTD4); this
      // page has no more work to do here.
      if (json.clientSecret && json.stripeAccountId) {
        setPaymentContext({
          referenceCode: json.referenceCode,
          clientSecret: json.clientSecret,
          stripeAccountId: json.stripeAccountId,
          createdAtMs: Date.now()
        })
        if (framed) setEmbedView({ kind: 'payment' })
        return
      }

      if (framed) {
        // Embed completion (R5/R7): report to the host and show an inline
        // confirmation with a persistent Done control — no page navigation.
        postToParent(CHECKOUT_MSG.ORDER_COMPLETE, eventPublicId, hostOriginRef.current, {
          orderId: json.referenceCode
        })
        const outstandingForms = hasBeforeAttendanceForms(waiverRequirements)
        setEmbedView({ kind: 'confirmation', orderId: json.referenceCode, outstandingForms })
        return
      }
      // BE-TICKETS-002: include buyer email so the confirmation page can
      // pass `?email=` through to the backend lookup, which now requires
      // a buyer-email match to return PII (404 on mismatch).
      router.push(confirmationPath(json.referenceCode))
    } catch (err) {
      // A thrown fetch (network) surfaces as a retriable submit failure on the
      // checkout surfaces — losing a filled-in form to an error boundary is
      // worse inside a modal or an in-app browser, where there is no obvious
      // way back. The plain page keeps its prior behavior (rethrow). Only the
      // framed surface has a loader to notify.
      if (surface === 'page') throw err
      if (framed) postToParent(CHECKOUT_MSG.SUBMIT_FAILED, eventPublicId, hostOriginRef.current)
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const submitLabel = submitting
    ? 'Submitting…'
    : isApplication
      ? 'Submit Application'
      : isSignup
        ? 'Sign Up'
        : 'Confirm RSVP'

  // Inline signing step (KTD2): render the at-registration form in place,
  // replacing the popup, for both the embed modal and the mobile in-app browser.
  if (inlineWaiver && embedView.kind === 'signing' && embedView.requirement.formPublicSlug && apiBase) {
    const req = embedView.requirement
    return (
      <InlineWaiverSigner
        apiBase={apiBase}
        formPublicSlug={req.formPublicSlug!}
        ticketedEventPublicId={eventPublicId}
        waiverRequirementPublicId={req.id}
        signerName={`${firstName.trim()} ${lastName.trim()}`}
        signerEmail={email.trim()}
        label={req.label}
        onSigned={result => onInlineSigned(req.id, result)}
        onBack={() => setEmbedView({ kind: 'form' })}
      />
    )
  }

  // Embed payment step (U8): Payment Element inline in the modal. Success
  // reports orderComplete + shows the SAME confirmation view free orders use;
  // amount/fee display is a non-embed (full confirmation page) concern.
  if (framed && embedView.kind === 'payment' && paymentContext) {
    return (
      <PaidCheckoutStep
        schoolSlug={schoolSlug}
        buyerEmail={email}
        paymentContext={paymentContext}
        // A true off-domain SCA redirect is rare and would break the iframe
        // illusion regardless — the return_url still lands on the real
        // confirmation page (with `acct` so it can display the amount paid)
        // rather than a dead end.
        returnUrl={`${window.location.origin}${confirmationPath(paymentContext.referenceCode)}&acct=${encodeURIComponent(paymentContext.stripeAccountId)}`}
        onSucceeded={() => {
          postToParent(CHECKOUT_MSG.ORDER_COMPLETE, eventPublicId, hostOriginRef.current, {
            orderId: paymentContext.referenceCode
          })
          setEmbedView({
            kind: 'confirmation',
            orderId: paymentContext.referenceCode,
            outstandingForms: hasBeforeAttendanceForms(waiverRequirements)
          })
        }}
        onRestart={() => {
          setPaymentContext(null)
          setEmbedView({ kind: 'form' })
        }}
      />
    )
  }

  // Embed confirmation step (R7): persistent Done control, no navigation.
  if (framed && embedView.kind === 'confirmation') {
    return (
      <EmbedConfirmationStep
        orderId={embedView.orderId}
        outstandingForms={embedView.outstandingForms}
        onDone={() => postToParent(CHECKOUT_MSG.CLOSE, eventPublicId, hostOriginRef.current)}
      />
    )
  }

  // Unframed payment step (U8): replaces the form in place, then navigates to
  // /confirmation on success — the page and mobile surfaces both take this path,
  // which is also what gets mobile buyers to the `before_attendance` forms the
  // embed's in-modal confirmation cannot host. Stripe's
  // `payment_intent`/`redirect_status` params are appended manually here for the
  // non-redirect success path so the confirmation page's handling is uniform
  // regardless of whether Stripe itself performed the return-url redirect (see
  // payment-status-panel.tsx).
  if (!framed && paymentContext) {
    return (
      <PaidCheckoutStep
        schoolSlug={schoolSlug}
        buyerEmail={email}
        paymentContext={paymentContext}
        returnUrl={`${window.location.origin}${confirmationPath(paymentContext.referenceCode)}&acct=${encodeURIComponent(paymentContext.stripeAccountId)}`}
        onSucceeded={(info: CheckoutSucceededInfo) =>
          router.push(
            `${confirmationPath(paymentContext.referenceCode)}&acct=${encodeURIComponent(paymentContext.stripeAccountId)}&payment_intent_client_secret=${encodeURIComponent(info.paymentIntentClientSecret)}&redirect_status=${encodeURIComponent(info.status)}`
          )
        }
        onRestart={() => setPaymentContext(null)}
      />
    )
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={onSubmit}>
      <h2 className="text-xl font-semibold">{isSignup ? 'Sign up' : isApplication ? 'Apply' : 'RSVP'}</h2>

      {ticketTypes.length > 0 && !isSignup && (
        <div className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Tickets</h3>
          <div className="rounded-lg border divide-y">
            {ticketTypes.map(t => {
              const max = Math.min(t.perOrderMax, t.capacityRemaining, orderPerOrderMax)
              const q = quantities[t.id] ?? 0
              const soldOut = t.capacityRemaining <= 0
              return (
                <div key={t.id} className="flex items-start justify-between gap-4 p-4">
                  <div className="flex-1">
                    <div className="font-medium">{t.name}</div>
                    {t.description && <div className="text-sm text-muted-foreground mt-1">{t.description}</div>}
                    <div className="text-sm mt-1">
                      {t.priceCents != null ? `$${(t.priceCents / 100).toFixed(2)}` : 'Free'}
                      {soldOut && <span className="ml-2 text-destructive">Sold out</span>}
                      {!soldOut && <span className="ml-2 text-muted-foreground">{availabilityLabel(t)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={q <= 0}
                      onClick={() => setQty(t.id, q - 1, max)}
                    >
                      −
                    </Button>
                    <Input
                      type="number"
                      min={0}
                      max={max}
                      value={q}
                      disabled={soldOut}
                      onChange={e => setQty(t.id, Number(e.target.value), max)}
                      className="w-16 text-center"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={q >= max || soldOut}
                      onClick={() => setQty(t.id, q + 1, max)}
                    >
                      +
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
          <p className="text-xs text-muted-foreground">Up to {orderPerOrderMax} per order.</p>
        </div>
      )}

      {isSignup && groupedTypes && (
        <div className="flex flex-col gap-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Available slots</h3>
          {groupedTypes.map(([groupLabel, types]) => (
            <div key={groupLabel || '_'} className="flex flex-col gap-2">
              {groupLabel && <div className="text-sm font-medium">{groupLabel}</div>}
              <div className="rounded-lg border divide-y">
                {types.map(t => {
                  const soldOut = t.capacityRemaining <= 0
                  const selected = (quantities[t.id] ?? 0) > 0
                  return (
                    <div key={t.id} className="flex items-center justify-between gap-4 p-4">
                      <div className="flex-1">
                        <div className="font-medium">{t.name}</div>
                        {(t.slotStartsAt || t.slotEndsAt) && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {slotTimeLine(t, event.school.timezone)}
                          </div>
                        )}
                        {t.description && <div className="text-sm text-muted-foreground mt-1">{t.description}</div>}
                        <div className="text-xs text-muted-foreground mt-1">
                          {soldOut ? 'Filled' : availabilityLabel(t)}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant={selected ? 'default' : 'outline'}
                        disabled={soldOut}
                        onClick={() => setQty(t.id, selected ? 0 : 1, 1)}
                      >
                        {selected ? 'Selected' : soldOut ? 'Filled' : 'Sign up'}
                      </Button>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Your details</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="firstName">First name</Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              disabled={buyerLocked}
              required
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="lastName">Last name</Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              disabled={buyerLocked}
              required
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={emailLocked}
            required
          />
          {buyerLocked && (
            <p className="text-xs text-muted-foreground">
              Locked because a waiver has been signed.{' '}
              <button type="button" className="underline hover:text-foreground" onClick={clearAllSignatures}>
                Change email & re-sign
              </button>
            </p>
          )}
        </div>
      </div>

      {event.requirePerAttendeeData && attendees.length > 0 && (
        <div className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Attendees</h3>
          {attendees.map((a, idx) => (
            <div key={idx} className="rounded-lg border p-4 flex flex-col gap-3">
              <div className="text-sm font-medium">Attendee {idx + 1}</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`a-fn-${idx}`}>First name</Label>
                  <Input
                    id={`a-fn-${idx}`}
                    value={a.firstName}
                    onChange={e =>
                      setAttendees(prev => prev.map((x, i) => (i === idx ? { ...x, firstName: e.target.value } : x)))
                    }
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`a-ln-${idx}`}>Last name</Label>
                  <Input
                    id={`a-ln-${idx}`}
                    value={a.lastName}
                    onChange={e =>
                      setAttendees(prev => prev.map((x, i) => (i === idx ? { ...x, lastName: e.target.value } : x)))
                    }
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`a-em-${idx}`}>Email (optional)</Label>
                  <Input
                    id={`a-em-${idx}`}
                    type="email"
                    value={a.email}
                    onChange={e =>
                      setAttendees(prev => prev.map((x, i) => (i === idx ? { ...x, email: e.target.value } : x)))
                    }
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`a-gr-${idx}`}>Grade (optional)</Label>
                  <Input
                    id={`a-gr-${idx}`}
                    value={a.grade}
                    onChange={e =>
                      setAttendees(prev => prev.map((x, i) => (i === idx ? { ...x, grade: e.target.value } : x)))
                    }
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {customQuestions.length > 0 && (
        <div className="flex flex-col gap-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Additional questions</h3>
          {customQuestions.map(q => (
            <CustomQuestionField
              key={q.id}
              question={q}
              value={answers[q.id]}
              onChange={v => setAnswers(prev => ({ ...prev, [q.id]: v }))}
            />
          ))}
        </div>
      )}

      {waiverRequirements.length > 0 && (
        <div className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Waivers</h3>
          <div className="rounded-lg border divide-y">
            {waiverRequirements.map(w => {
              const sig = signatures[w.id]
              const pending = waiverPending[w.id]
              const signable = !!w.formPublicSlug && !w.perAttendee
              const buttonLabel = sig
                ? `Signed by ${sig.signerName} — re-sign`
                : pending === 'opening'
                  ? 'Opening waiver…'
                  : pending === 'pending'
                    ? 'Waiting for signature…'
                    : 'Sign Waiver'
              return (
                <div key={w.id} className="flex items-center justify-between gap-4 p-4">
                  <div className="flex-1">
                    <div className="font-medium">{w.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {w.required ? 'Required' : 'Optional'}
                      {w.perAttendee && ' · per attendee (not yet supported)'}
                    </div>
                    {!signable && !w.perAttendee && (
                      <div className="text-xs text-destructive mt-1">
                        This waiver isn’t available to sign right now. Please contact the school.
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant={sig ? 'outline' : 'default'}
                    disabled={!signable || !!pending}
                    onClick={() => openWaiverPopup(w)}
                  >
                    {buttonLabel}
                  </Button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" disabled={submitting} size="lg">
        {submitLabel}
      </Button>
    </form>
  )
}

// Embed-only terminal view: the parent page keeps the modal open, so this is a
// dead end with a Done control rather than a navigation.
function EmbedConfirmationStep({
  orderId,
  outstandingForms,
  onDone
}: {
  orderId: string
  outstandingForms: boolean
  onDone: () => void
}) {
  return (
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      <div className="text-2xl">🎉</div>
      <h2 className="text-xl font-semibold">You’re registered!</h2>
      <p className="text-sm text-muted-foreground">
        Your confirmation code is <span className="font-mono font-medium">{orderId}</span>. A confirmation email is on
        its way.
      </p>
      {outstandingForms && (
        <p className="max-w-sm text-sm text-muted-foreground">
          One or more required forms are due before the event — you’ll receive them by email.
        </p>
      )}
      <Button type="button" size="lg" onClick={onDone}>
        Done
      </Button>
    </div>
  )
}

// U8: the shared Payment Element wiring for both the embed and non-embed paid
// branches — identical prop threading from `PaymentContext`, differing only in
// the `returnUrl` + `onSucceeded`/`onRestart` the caller supplies.
function PaidCheckoutStep({
  schoolSlug,
  buyerEmail,
  paymentContext,
  returnUrl,
  onSucceeded,
  onRestart
}: {
  schoolSlug: string
  buyerEmail: string
  paymentContext: PaymentContext
  returnUrl: string
  onSucceeded: (info: CheckoutSucceededInfo) => void
  onRestart: () => void
}) {
  return (
    <CheckoutPaymentStep
      schoolSlug={schoolSlug}
      referenceCode={paymentContext.referenceCode}
      buyerEmail={buyerEmail}
      clientSecret={paymentContext.clientSecret}
      stripeAccountId={paymentContext.stripeAccountId}
      createdAtMs={paymentContext.createdAtMs}
      returnUrl={returnUrl}
      onSucceeded={onSucceeded}
      onRestart={onRestart}
    />
  )
}

interface CustomQuestionFieldProps {
  question: TicketedEventCustomQuestionPublic
  value: string | string[] | boolean | undefined
  onChange: (v: string | string[] | boolean) => void
}

function CustomQuestionField({ question, value, onChange }: CustomQuestionFieldProps) {
  const id = `q-${question.id}`
  const labelEl = (
    <Label htmlFor={id}>
      {question.prompt}
      {question.required && <span className="text-destructive ml-1">*</span>}
    </Label>
  )

  if (question.type === 'short_text') {
    return (
      <div className="flex flex-col gap-2">
        {labelEl}
        <Input
          id={id}
          value={(value as string) ?? ''}
          onChange={e => onChange(e.target.value)}
          required={question.required}
        />
      </div>
    )
  }
  if (question.type === 'long_text') {
    return (
      <div className="flex flex-col gap-2">
        {labelEl}
        <Textarea
          id={id}
          value={(value as string) ?? ''}
          onChange={e => onChange(e.target.value)}
          required={question.required}
          rows={4}
        />
      </div>
    )
  }
  if (question.type === 'single_select') {
    return (
      <div className="flex flex-col gap-2">
        {labelEl}
        {(question.options?.length ?? 0) <= 4 ? (
          <RadioGroup value={(value as string) ?? ''} onValueChange={v => onChange(v)}>
            {(question.options ?? []).map(opt => (
              <div key={opt} className="flex items-center gap-2">
                <RadioGroupItem id={`${id}-${opt}`} value={opt} />
                <Label htmlFor={`${id}-${opt}`} className="font-normal">
                  {opt}
                </Label>
              </div>
            ))}
          </RadioGroup>
        ) : (
          <Select value={(value as string) ?? ''} onValueChange={v => onChange(v)}>
            <SelectTrigger id={id}>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {(question.options ?? []).map(opt => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    )
  }
  if (question.type === 'multi_select') {
    const arr = Array.isArray(value) ? (value as string[]) : []
    return (
      <div className="flex flex-col gap-2">
        {labelEl}
        <div className="flex flex-col gap-2">
          {(question.options ?? []).map(opt => {
            const checked = arr.includes(opt)
            return (
              <div key={opt} className="flex items-center gap-2">
                <Checkbox
                  id={`${id}-${opt}`}
                  checked={checked}
                  onCheckedChange={c => {
                    const next = c ? [...arr, opt] : arr.filter(x => x !== opt)
                    onChange(next)
                  }}
                />
                <Label htmlFor={`${id}-${opt}`} className="font-normal">
                  {opt}
                </Label>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
  // checkbox
  return (
    <div className="flex items-start gap-2">
      <Checkbox id={id} checked={value === true} onCheckedChange={c => onChange(c === true)} />
      <Label htmlFor={id} className="font-normal">
        {question.prompt}
        {question.required && <span className="text-destructive ml-1">*</span>}
      </Label>
    </div>
  )
}
